<script setup lang="ts">
/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useLayout } from '@/layout/composables/layout';
import { apiClient } from '@/api/client';
import { useAuthStore } from '@/stores/auth';

const { layoutState } = useLayout();
const auth = useAuthStore();
const { t } = useI18n();

// Operatori timbrati in entrata oggi (tipo "presenti" del widget Personale).
interface OperatorePresente {
  id: number;
  nome: string | null;
  cognome: string | null;
  qualifica: string | null;
  centroId: number | null;
  centroNome: string | null;
  orario: string | null; // HH:mm timbratura di entrata
}

const operatori = ref<OperatorePresente[]>([]);
const loading = ref(false);
// id operatori la cui foto non è disponibile (404) → fallback iniziali.
const fotoErrate = ref<Set<number>>(new Set());

// Stessa condizione del widget Personale: dati timbrature/personale → richiede PERSONALE_READ
// (l'endpoint /personale-dettaglio è gated PERSONALE_READ).
const canSee = computed(() => auth.has('PERSONALE_READ'));

async function load(): Promise<void> {
  if (!canSee.value) return;
  loading.value = true;
  fotoErrate.value = new Set(); // ricarica: riprova le foto
  try {
    const res = await apiClient.get<OperatorePresente[]>('/api/v2/dashboard/personale-dettaglio', {
      params: { tipo: 'presenti' },
    });
    operatori.value = res.data ?? [];
  } catch {
    // errori gestiti dall'interceptor globale
  } finally {
    loading.value = false;
  }
}

// Ricarica a ogni apertura del drawer: i "presenti" sono lo stato corrente della giornata.
watch(
  () => layoutState.rightMenuActive,
  (open) => {
    if (open) void load();
  }
);

function iniziali(o: OperatorePresente): string {
  return ((o.cognome?.[0] ?? '') + (o.nome?.[0] ?? '')).toUpperCase() || '?';
}
function nomeCompleto(o: OperatorePresente): string {
  return [o.cognome, o.nome].filter(Boolean).join(' ') || t('layout.rightMenu.operatore', 'Operatore');
}
function fotoUrl(id: number): string {
  return `/api/v2/personale/${id}/foto`;
}
function mostraFoto(id: number): boolean {
  return !fotoErrate.value.has(id);
}
function onFotoError(id: number): void {
  fotoErrate.value = new Set(fotoErrate.value).add(id);
}
</script>

<template>
  <Drawer
    v-model:visible="layoutState.rightMenuActive"
    position="right"
    :header="t('layout.rightMenu.titolo', 'Operatori in servizio')"
    class="w-80"
  >
    <template v-if="!canSee">
      <p class="text-sm" style="color: var(--p-text-muted-color)">
        {{ t('layout.rightMenu.nonDisponibile', 'Permesso non disponibile.') }}
      </p>
    </template>

    <div
      v-else
      class="flex flex-col gap-2"
    >
      <span class="pb-2 mb-1 border-b border-surface font-semibold text-sm">
        {{ t('layout.rightMenu.inServizioOggi', 'IN SERVIZIO OGGI') }}
        <span
          v-if="!loading"
          class="font-normal"
          style="color: var(--p-text-muted-color)"
        >({{ operatori.length }})</span>
      </span>

      <!-- Loading -->
      <template v-if="loading">
        <Skeleton
          v-for="n in 4"
          :key="n"
          height="44px"
          class="mb-1"
        />
      </template>

      <!-- Vuoto -->
      <div
        v-else-if="operatori.length === 0"
        class="flex items-center gap-2 text-sm py-3"
        style="color: var(--p-text-muted-color)"
      >
        <i class="pi pi-user-minus" />
        {{ t('layout.rightMenu.nessunOperatore', 'Nessun operatore timbrato oggi') }}
      </div>

      <!-- Lista operatori -->
      <ul
        v-else
        class="m-0 list-none p-0 flex flex-col gap-1"
      >
        <li
          v-for="o in operatori"
          :key="o.id"
          class="flex items-center gap-3 px-2 py-2 rounded-md hover:bg-emphasis"
        >
          <img
            v-if="mostraFoto(o.id)"
            :src="fotoUrl(o.id)"
            :alt="nomeCompleto(o)"
            class="op-avatar op-foto"
            @error="onFotoError(o.id)"
          >
          <span
            v-else
            class="op-avatar"
          >{{ iniziali(o) }}</span>
          <div class="flex flex-col min-w-0 flex-1">
            <span class="font-medium truncate">{{ nomeCompleto(o) }}</span>
            <span
              class="text-xs truncate"
              style="color: var(--p-text-muted-color)"
            >
              <template v-if="o.qualifica">{{ o.qualifica }} · </template>{{ o.centroNome ?? '—' }}
            </span>
          </div>
          <span
            v-if="o.orario"
            class="text-xs whitespace-nowrap"
            style="color: var(--p-primary-color)"
          >
            <i class="pi pi-clock mr-1" />{{ o.orario }}
          </span>
        </li>
      </ul>
    </div>
  </Drawer>
</template>

<style scoped>
.op-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  flex-shrink: 0;
  border-radius: 50%;
  font-size: 0.72rem;
  font-weight: 600;
  color: #fff;
  background: var(--p-primary-color);
}
.op-foto {
  object-fit: cover;
  background: var(--p-surface-200);
}
</style>
